/**
 * The lazy boundary around Apollo.
 *
 * `./graphql` is the only module that imports `@apollo/client` (and, through
 * `gql`, the `graphql` parser) — ~300 KB uncompressed. Nothing needs any of it
 * until a query actually runs: a sign-in, a token refresh, a profile read. Yet
 * `App.tsx` wraps every route, published sites included, and used to import it
 * statically to call `initializeGQLClient()` at boot, so every visitor paid for
 * it on the render-blocking path before the first paint.
 *
 * Boot code calls `configureGQL()` — a plain assignment, no Apollo — and every
 * consumer goes through `loadGQL()`, which imports `./graphql` on first use and
 * applies the stored config once. `scripts/check-apollo-boundary.mjs` keeps it
 * that way: no static import of `@apollo/client` or `./graphql` outside this
 * file and the server-only allowlist.
 *
 * The config is held by reference. `authTool` is a mutable singleton that
 * `App.tsx` patches after boot, so the module sees the live methods whenever it
 * loads — the same object `initializeGQLClient` always received.
 */

type GqlModule = typeof import('./graphql');
type AuthTool = NonNullable<Parameters<GqlModule['initializeGQLClient']>[1]>;

interface GqlConfig {
  GRAPHQL: string | undefined;
  authTool?: AuthTool;
}

let pendingConfig: GqlConfig | null = null;
let loaded: GqlModule | null = null;
let loading: Promise<GqlModule> | null = null;

/**
 * What `initializeGQLClient` used to be called with, from the same places (boot
 * in `App.tsx`, and again after sign-in in `auth-utils`). Applied on load, or
 * immediately if the module is already in.
 */
export function configureGQL(GRAPHQL: string | undefined, authTool?: AuthTool): void {
  pendingConfig = { GRAPHQL, authTool };
  if (loaded) loaded.initializeGQLClient(GRAPHQL, authTool);
}

/** The Apollo module, loaded once on first use with the boot config applied. */
export function loadGQL(): Promise<GqlModule> {
  if (loaded) return Promise.resolve(loaded);
  if (!loading) {
    loading = import('./graphql').then((mod) => {
      loaded = mod;
      if (pendingConfig) mod.initializeGQLClient(pendingConfig.GRAPHQL, pendingConfig.authTool);
      return mod;
    });
  }
  return loading;
}
